/**
 * SettingsSiteLanguages — Iter 191e.
 * Pick a primary site language + connect a translation service.
 * Persistence: PUT /api/builder/settings { primary_language, translation_provider, translation_api_key }.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Loader2, Check, ExternalLink, Languages } from 'lucide-react';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LANGUAGES = [
  { code: 'en', label: 'English',    region: 'United States' },
  { code: 'en-GB', label: 'English', region: 'United Kingdom' },
  { code: 'es', label: 'Español',    region: 'Spanish' },
  { code: 'fr', label: 'Français',   region: 'French' },
  { code: 'de', label: 'Deutsch',    region: 'German' },
  { code: 'it', label: 'Italiano',   region: 'Italian' },
  { code: 'pt', label: 'Português',  region: 'Portuguese (Portugal)' },
  { code: 'pt-BR', label: 'Português', region: 'Portuguese (Brazil)' },
  { code: 'nl', label: 'Nederlands', region: 'Dutch' },
  { code: 'sv', label: 'Svenska',    region: 'Swedish' },
  { code: 'no', label: 'Norsk',      region: 'Norwegian' },
  { code: 'da', label: 'Dansk',      region: 'Danish' },
  { code: 'fi', label: 'Suomi',      region: 'Finnish' },
  { code: 'pl', label: 'Polski',     region: 'Polish' },
  { code: 'cs', label: 'Čeština',    region: 'Czech' },
  { code: 'ru', label: 'Русский',    region: 'Russian' },
  { code: 'uk', label: 'Українська', region: 'Ukrainian' },
  { code: 'tr', label: 'Türkçe',     region: 'Turkish' },
  { code: 'ar', label: 'العربية',    region: 'Arabic' },
  { code: 'he', label: 'עברית',      region: 'Hebrew' },
  { code: 'hi', label: 'हिन्दी',     region: 'Hindi' },
  { code: 'bn', label: 'বাংলা',       region: 'Bengali' },
  { code: 'ur', label: 'اردو',       region: 'Urdu' },
  { code: 'zh-CN', label: '简体中文', region: 'Chinese (Simplified)' },
  { code: 'zh-TW', label: '繁體中文', region: 'Chinese (Traditional)' },
  { code: 'ja', label: '日本語',     region: 'Japanese' },
  { code: 'ko', label: '한국어',      region: 'Korean' },
  { code: 'th', label: 'ไทย',        region: 'Thai' },
  { code: 'vi', label: 'Tiếng Việt', region: 'Vietnamese' },
  { code: 'id', label: 'Bahasa Indonesia', region: 'Indonesian' },
  { code: 'ms', label: 'Bahasa Melayu', region: 'Malay' },
  { code: 'sw', label: 'Kiswahili',  region: 'Swahili' },
  { code: 'zu', label: 'isiZulu',    region: 'Zulu' },
  { code: 'nv', label: 'Diné Bizaad', region: 'Navajo' },
  { code: 'chr', label: 'ᏣᎳᎩ ᎦᏬᏂᎯᏍᏗ', region: 'Cherokee' },
  { code: 'mi', label: 'Māori',      region: 'New Zealand Māori' },
  { code: 'haw', label: 'ʻŌlelo Hawaiʻi', region: 'Hawaiian' },
];

const PROVIDERS = [
  { id: 'weglot',   name: 'Weglot',    desc: 'One-click translation + editor. Free up to 2000 words.', url: 'https://weglot.com/pricing', tier: 'Popular' },
  { id: 'localize', name: 'Localize',  desc: 'Automatic + human review. Enterprise-friendly.',         url: 'https://localizejs.com/pricing' },
  { id: 'gtranslate', name: 'GTranslate', desc: 'Google-powered. Free tier with limited languages.',  url: 'https://gtranslate.io/pricing' },
  { id: 'deepl',    name: 'DeepL API', desc: 'Highest quality neural translation. Pro from $8/mo.',    url: 'https://deepl.com/pro-api' },
  { id: 'crowdin',  name: 'Crowdin',   desc: 'Community + AI. Best for open-source & docs sites.',     url: 'https://crowdin.com/pricing' },
];

export default function SettingsSiteLanguages() {
  const [primary, setPrimary] = useState('en');
  const [provider, setProvider] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/builder/settings`, { headers: { Authorization: `Bearer ${token}` } });
        setPrimary(res.data?.primary_language || 'en');
        setProvider(res.data?.translation_provider || '');
        setApiKey(res.data?.translation_api_key || '');
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, [token]);

  const persist = async (patch) => {
    setSaving(true);
    try { await axios.put(`${API}/builder/settings`, patch, { headers: { Authorization: `Bearer ${token}` } }); }
    catch (_) { toast.error('Could not save'); }
    setSaving(false);
  };

  const filtered = LANGUAGES.filter(l => !query || (l.label + ' ' + l.region).toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-full" data-testid="settings-languages-page">
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Settings</h1>
        </div>
        {saving && <span className="text-[10px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold animate-pulse">Saving…</span>}
      </div>

      <div className="max-w-4xl mx-auto px-8 py-10">
        <div className="text-[12px] text-[#64748B] mb-3">
          <span className="text-white/70 font-semibold">Website</span>
          <span className="mx-2">/</span>
          <span className="text-white">Site Languages</span>
        </div>
        <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>Site Languages</h2>
        <p className="mt-3 text-[14px] text-[#9CA3AF] leading-relaxed max-w-2xl">
          Choose the primary language your site is written in, then connect a translation service to auto-translate for international visitors.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-white/50"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* Primary language picker */}
            <section className="mt-8 rounded-xl bg-white/[0.03] border border-white/[0.08] p-6">
              <div className="text-[10.5px] uppercase tracking-[0.20em] text-[#64748B] font-bold mb-3">Primary Language</div>
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search languages…"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-[13px] text-white placeholder-white/35 focus:outline-none focus:border-[#A78BFA]/50"
                  data-testid="lang-search"
                />
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-[380px] overflow-y-auto pr-2">
                {filtered.map((l) => {
                  const active = primary === l.code;
                  return (
                    <button
                      key={l.code}
                      onClick={() => { setPrimary(l.code); persist({ primary_language: l.code }); toast.success(`Primary set to ${l.label}`); }}
                      className={`flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all text-left ${
                        active ? 'bg-[#A78BFA]/15 border-[#A78BFA]/50' : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.05] hover:border-white/15'
                      }`}
                      data-testid={`lang-${l.code}`}
                    >
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-white truncate">{l.label}</div>
                        <div className="text-[10.5px] text-[#64748B] truncate">{l.region}</div>
                      </div>
                      {active && <Check size={14} className="text-[#A78BFA] flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {filtered.length === 0 && (
                <p className="mt-4 text-[12px] text-white/45">No languages match &quot;{query}&quot;</p>
              )}
            </section>

            {/* Translation providers */}
            <section className="mt-8 rounded-xl bg-white/[0.03] border border-white/[0.08] p-6">
              <div className="flex items-center gap-2 mb-1">
                <Languages size={14} className="text-[#A78BFA]" />
                <div className="text-[10.5px] uppercase tracking-[0.20em] text-[#64748B] font-bold">Translation Service</div>
              </div>
              <p className="text-[13px] text-[#9CA3AF] mb-5 max-w-2xl leading-relaxed">
                Connect a third-party translation service to auto-translate your site for international visitors.
                Each provider bills you directly for their subscription — we just pass the API key through to your public site.
              </p>
              <div className="space-y-2">
                {PROVIDERS.map((p) => {
                  const active = provider === p.id;
                  return (
                    <div
                      key={p.id}
                      className={`p-4 rounded-xl border transition-all ${
                        active ? 'bg-[#A78BFA]/10 border-[#A78BFA]/50' : 'bg-white/[0.02] border-white/[0.08]'
                      }`}
                      data-testid={`provider-${p.id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[14px] font-semibold text-white">{p.name}</span>
                            {p.tier && <span className="px-2 py-[3px] rounded-md text-[9px] font-bold uppercase tracking-[0.14em] bg-[#F59E0B]/15 text-[#FBBF24] border border-[#F59E0B]/25">{p.tier}</span>}
                          </div>
                          <p className="mt-1 text-[12px] text-[#9CA3AF]">{p.desc}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-semibold text-[#A78BFA] hover:text-white hover:bg-white/[0.06] border border-white/10 transition-all" data-testid={`provider-plans-${p.id}`}>
                            View plans <ExternalLink size={10} />
                          </a>
                          {active ? (
                            <button onClick={() => { setProvider(''); persist({ translation_provider: '' }); toast.success(`${p.name} disconnected`); }} className="px-3 py-1.5 rounded-md bg-red-500/15 border border-red-500/25 text-red-300 text-[11px] font-bold uppercase tracking-wider hover:bg-red-500/25 transition-all" data-testid={`provider-disconnect-${p.id}`}>
                              Disconnect
                            </button>
                          ) : (
                            <button onClick={() => { setProvider(p.id); persist({ translation_provider: p.id }); toast.success(`${p.name} selected — add your API key`); }} className="px-3 py-1.5 rounded-md bg-[#A78BFA] text-[#0E0E23] text-[11px] font-bold uppercase tracking-wider hover:bg-white transition-all" data-testid={`provider-connect-${p.id}`}>
                              Connect
                            </button>
                          )}
                        </div>
                      </div>
                      {active && (
                        <div className="mt-3 flex items-center gap-2">
                          <input
                            type="password"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            onBlur={() => persist({ translation_api_key: apiKey })}
                            placeholder={`${p.name} API key`}
                            className="flex-1 bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#A78BFA]/60"
                            data-testid={`provider-key-${p.id}`}
                          />
                          <button onClick={() => { persist({ translation_api_key: apiKey }); toast.success('API key saved'); }} className="px-3 py-2 rounded-md bg-[#A78BFA] text-[#0E0E23] text-[11px] font-bold uppercase tracking-wider hover:bg-white transition-all">
                            Save
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="mt-5 text-[11px] text-[#64748B]">
                We don&apos;t charge for translation — you connect directly with the provider and manage billing on their platform.
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
